import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getQuotes, deleteQuote, getQuotesCsvUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDate } from '../lib/utils';
import { Plus, MagnifyingGlass, Eye, FileText, User, Trash, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const statusConfig = {
    draft: { label: 'Draft', variant: 'secondary' },
    internal_review: { label: 'Review', variant: 'default' },
    sent: { label: 'Sent', variant: 'default' },
    revised: { label: 'Revised', variant: 'outline' },
    won: { label: 'Won', variant: 'default' },
    lost: { label: 'Lost', variant: 'destructive' },
    cancelled: { label: 'Cancelled', variant: 'destructive' },
    archived: { label: 'Archived', variant: 'outline' },
};

export default function QuoteList() {
    const { can, canViewAllQuotes } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState(canViewAllQuotes() ? 'all' : 'mine');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (search) params.search = search;
            params.scope = scope;
            const res = await getQuotes(params);
            setQuotes(res.data.quotes);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [statusFilter, search, scope]);

    useEffect(() => { load(); }, [load]);

    const canCreate = can('create_quotes');
    const canDelete = can('delete_quotes');

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteQuote(deleteTarget.id);
            toast.success(`Quote ${deleteTarget.quote_number} deleted`);
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete quote');
        }
    };

    return (
        <div className="space-y-6" data-testid="quote-list-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Quotations</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage commercial quotations</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => window.open(getQuotesCsvUrl(), '_blank')} data-testid="export-csv-btn">
                        <DownloadSimple size={16} className="mr-1" />Export CSV
                    </Button>
                    {canCreate && (
                        <Button asChild data-testid="new-quote-btn">
                            <Link to="/quotes/new"><Plus size={18} className="mr-2" />New Quote</Link>
                        </Button>
                    )}
                </div>
            </div>

            {/* Scope toggle + Search */}
            <div className="flex items-center gap-3 flex-wrap">
                {canViewAllQuotes() && (
                    <div className="flex items-center bg-muted rounded-md p-0.5" data-testid="scope-toggle">
                        <button
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scope === 'mine' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => setScope('mine')}
                            data-testid="scope-mine"
                        >
                            <User size={12} className="inline mr-1" />My Quotes
                        </button>
                        <button
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scope === 'all' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => setScope('all')}
                            data-testid="scope-all"
                        >
                            All Quotes
                        </button>
                    </div>
                )}
                <div className="relative flex-1 max-w-sm">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search quotes..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="quote-search-input"
                    />
                </div>
            </div>

            {/* Status filter tabs */}
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList data-testid="status-tabs">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="draft">Draft</TabsTrigger>
                    <TabsTrigger value="internal_review">Review</TabsTrigger>
                    <TabsTrigger value="sent">Sent</TabsTrigger>
                    <TabsTrigger value="won">Won</TabsTrigger>
                    <TabsTrigger value="lost">Lost</TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Table */}
            <div className="bg-card border border-border rounded-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Quote #</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Lines</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead>Created By</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="w-20"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-8" /></TableCell></TableRow>
                        )) : quotes.length === 0 ? (
                            <TableRow><TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                {scope === 'mine' ? 'No quotes found. Create your first quote.' : 'No quotes match your filter.'}
                            </TableCell></TableRow>
                        ) : quotes.map(q => {
                            const sc = statusConfig[q.status] || {};
                            return (
                                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                                    <TableCell>
                                        <Link to={`/quotes/${q.id}`} className="font-mono text-sm font-semibold hover:text-primary transition-colors" data-testid={`quote-link-${q.id}`}>
                                            {q.quote_number}
                                        </Link>
                                        <div className="text-xs text-muted-foreground">V{q.version}</div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="font-medium text-sm">{q.customer_name || '—'}</div>
                                        {q.customer_country && <div className="text-xs text-muted-foreground">{q.customer_country}</div>}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={sc.variant} className="capitalize text-[10px]">{sc.label || q.status}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-sm">{q.total_lines || 0}</TableCell>
                                    <TableCell className="text-right font-mono text-sm font-medium">
                                        {formatCurrency(q.total_amount || q.total || 0, q.currency || 'EUR')}
                                    </TableCell>
                                    <TableCell>
                                        <div className="text-xs text-muted-foreground">{q.created_by_name || '—'}</div>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{formatDate(q.created_at)}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                                <Link to={`/quotes/${q.id}`}><Eye size={14} /></Link>
                                            </Button>
                                            {canDelete && q.status === 'draft' && (
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(q)} data-testid={`delete-quote-${q.id}`}>
                                                    <Trash size={14} />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
                <AlertDialogContent data-testid="delete-quote-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Quote</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <span className="font-semibold">{deleteTarget?.quote_number}</span>? This will permanently remove the quote and all its line items. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
